import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core'
import { BehaviorSubject, map, Observable, shareReplay, Subject, take, tap } from 'rxjs'
import { SportsDataService } from '../sports-data.service'
import { SegmentService } from '../../shared/analytics/services/segment.service'
import {
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  PageMetaService
} from 'src/app/libs/page-meta/page-meta.service'
import type { SegmentTagV2, ListPageProperties } from '../../shared/analytics/models/segment-tag.model'
import type { MessageModel, SEOMetadata } from '@flocasts/experience-service-types'
import { isMessageModel, isMultipleSectionsViewModel } from 'src/app/shared/functions/type-guards'

// Need for routing module - so floAnalytics picks up these properties
export const SPORTS_LIST_SEGMENT_TAG: SegmentTagV2 = {
  page_category: 'Sports',
  category: 'Sports',
  autoPageCall: false
}

@Component({
  selector: 'flo-sports-list',
  templateUrl: './sport-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SportListComponent implements OnDestroy {
  constructor(
    private readonly sportsDataService: SportsDataService,
    private readonly segmentService: SegmentService,
    private readonly pageMetaService: PageMetaService
  ) {}

  private readonly ngOnDestroy$ = new Subject<void>()

  // if layout service fails, display error
  public readonly isLayoutError$ = new BehaviorSubject<boolean>(false)

  public sportsLayout$ = this.sportsDataService.getSportsLayout().pipe(
    tap(layout => {
      !layout ? this.isLayoutError$.next(true) : this.isLayoutError$.next(false)
    }),
    shareReplay(1)
  )

  private setPageMeta(seo: SEOMetadata): void {
    this.pageMetaService.setPageMeta({
      type: 'website',
      twitterCard: 'summary_large_image',
      title: seo.title || 'Sports',
      description: seo.description || '',
      canonicalUrl: seo.canonicalUrl || '',
      siteName: null,
      image: {
        url: seo.imageUrl || '',
        height: Number(seo.imageHeight) || DEFAULT_OG_IMAGE_HEIGHT,
        width: Number(seo.imageWidth) || DEFAULT_OG_IMAGE_WIDTH
      }
    })
  }

  private seoPageMeta$ = this.sportsLayout$
    .pipe(
      take(1),
      map(res => res?.seo),
      tap(seo => {
        if (!!seo) {
          this.setPageMeta(seo)
        }
      })
    )
    .subscribe() // unsubscribed by take(1)

  private analyticsPageCall$ = this.sportsLayout$
    .pipe(
      take(1),
      map(res => res?.analytics),
      tap(analytics => {
        const segmentTag: SegmentTagV2 = {
          ...SPORTS_LIST_SEGMENT_TAG,
          page_category: analytics?.extraContext?.pageCategory || 'Sports'
        }

        const segmentPageProperties: ListPageProperties = { _tag: 'list' }
        this.segmentService.pageV2(segmentTag, segmentPageProperties)
      })
    )
    .subscribe() // take(1) is unsubscribing

  public message$: Observable<MessageModel | undefined> = this.sportsLayout$.pipe(
    map(res => {
      if (res && isMultipleSectionsViewModel(res)) {
        return res.sections.find(isMessageModel)
      }
      return
    })
  )

  public ngOnDestroy() {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
    this.isLayoutError$.complete()
    this.pageMetaService.clearMeta()
  }
}
